// PATH: src/components/documents/chapterImport.ts
/**
 * Import d'un chapitre (Markdown + manifeste JSON séparé) : logique pure.
 *
 * L'aperçu est une LECTURE du manifeste, rien de plus : le serveur valide
 * (codes du catalogue, nombre de leçons) et fait foi. Aucun code de programme,
 * de niveau, de matière ou de filière n'est connu ici.
 *
 * Le manifeste ne porte que la STRUCTURE (leçons, ordre, titres) : les
 * objectifs sont déduits du cours à l'extraction. Des `lecons[].objectifs`
 * d'un ancien manifeste sont tolérés et ignorés par le serveur ; l'aperçu les
 * compte seulement pour le dire.
 */

export interface ManifestLessonPreview {
  ossatureRef: string;
  ordre: number | null;
  titre: string;
}

export interface ManifestPreview {
  chapterRef: string;
  program: string;
  grade: string;
  subject: string;
  /** null = chapitre commun à toutes les filières. */
  track: string | null;
  language: string;
  semester: number | null;
  chapterOrder: number | null;
  titre: string;
  lessons: ManifestLessonPreview[];
  /** Objectifs encore présents dans le manifeste (ancien format) : ignorés. */
  ignoredObjectives: number;
  /** Champs structurels absents ou vides : le serveur refusera (422). */
  missing: string[];
}

export type ManifestPreviewResult =
  | { ok: true; preview: ManifestPreview }
  | { ok: false; error: string };

const asString = (v: unknown): string => (typeof v === "string" ? v : "");
const asIntOrNull = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;
const isRecord = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === "object" && !Array.isArray(v);

/** Lit le manifeste pour l'aperçu. N'échoue que si ce n'est pas un objet JSON. */
export const parseManifestPreview = (text: string): ManifestPreviewResult => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    return {
      ok: false,
      error: `Manifeste illisible : ${e instanceof Error ? e.message : String(e)}`,
    };
  }
  if (!isRecord(parsed)) {
    return { ok: false, error: "Le manifeste doit être un objet JSON." };
  }
  const raw = parsed;

  const lecons = Array.isArray(raw.lecons) ? raw.lecons : [];
  let ignoredObjectives = 0;
  const lessons: ManifestLessonPreview[] = lecons.map((l) => {
    const lesson = isRecord(l) ? l : {};
    if (Array.isArray(lesson.objectifs)) ignoredObjectives += lesson.objectifs.length;
    return {
      ossatureRef: asString(lesson.ossature_ref),
      ordre: asIntOrNull(lesson.ordre),
      titre: asString(lesson.titre),
    };
  });

  const missing: string[] = [];
  (["chapter_ref", "program", "grade", "subject", "language", "titre"] as const).forEach((key) => {
    if (!asString(raw[key]).trim()) missing.push(key);
  });
  if (lessons.length === 0) missing.push("lecons");

  return {
    ok: true,
    preview: {
      chapterRef: asString(raw.chapter_ref),
      program: asString(raw.program),
      grade: asString(raw.grade),
      subject: asString(raw.subject),
      track: typeof raw.track === "string" && raw.track ? raw.track : null,
      language: asString(raw.language),
      semester: asIntOrNull(raw.semester),
      chapterOrder: asIntOrNull(raw.chapter_order),
      titre: asString(raw.titre),
      lessons,
      ignoredObjectives,
      missing,
    },
  };
};

/**
 * Nombre de lignes `# ` (titre de leçon) du Markdown. Indicatif : le contrat
 * veut autant de `#` que de leçons, et c'est le serveur qui tranche.
 */
export const countLessonHeadings = (markdown: string): number =>
  markdown.split(/\r?\n/).filter((line) => line.startsWith("# ")).length;

/** Corps multipart de `POST /v1/chapters/import`. */
export const buildChapterImportFormData = (fields: {
  file: File;
  manifest: File;
  isActive: boolean;
}): FormData => {
  const fd = new FormData();
  fd.append("manifest", fields.manifest, fields.manifest.name);
  fd.append("file", fields.file, fields.file.name);
  fd.append("is_active", fields.isActive ? "true" : "false");
  return fd;
};

/** Phrase de bilan après un import réussi. */
export const formatChapterImportResult = (res: {
  created: boolean;
  lessons: number;
  passages: number;
  version?: { version?: number } | null;
  document?: { title?: string } | null;
}): string => {
  const what = res.created
    ? "Chapitre créé"
    : `Nouvelle version${res.version?.version != null ? ` (v${res.version.version})` : ""} du chapitre existant`;
  const title = res.document?.title ? ` « ${res.document.title} »` : "";
  return `${what}${title} : ${res.lessons} leçon(s), ${res.passages} passage(s).`;
};

/** Message d'erreur lisible ; une 422 nomme le champ du manifeste en cause. */
export const formatChapterImportError = (err: unknown): string => {
  const e = err as {
    response?: { status?: number; data?: { error?: string; field?: string } };
    message?: string;
  };
  const data = e?.response?.data;
  const status = e?.response?.status;
  if (status === 422 && data?.error) {
    return data.field
      ? `Refusé (champ « ${data.field} ») : ${data.error}`
      : `Refusé : ${data.error}`;
  }
  return data?.error || e?.message || "Échec de l'import du chapitre.";
};
